import { useState, FormEvent } from 'react';
import Layout from '../../components/feature/Layout';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function FeedbackPage() {
  // SEO設定
  useSEO({
    title: 'フィードバック・お問い合わせ｜キョウコレ',
    description: 'キョウコレへのフィードバック、バグ報告、改善提案、お問い合わせはこちらから。皆様のご意見をお待ちしています。',
    keywords: 'フィードバック,お問い合わせ,バグ報告,改善提案,キョウコレ',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/feedback',
    jsonLd: generateWebPageSchema(
      'フィードバック・お問い合わせ｜キョウコレ',
      'キョウコレへのフィードバック、バグ報告、改善提案、お問い合わせはこちらから。皆様のご意見をお待ちしています。',
      '/feedback'
    ),
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    feedback: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.feedback.trim()) {
      alert('フィードバック内容を入力してください。');
      return;
    }

    if (formData.feedback.length > 500) {
      alert('フィードバック内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const form = e.currentTarget;
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('feedback', formData.feedback);

      const response = await fetch('https://readdy.ai/api/form/d4qi3var20r5k3vfkpk0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', feedback: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* ページタイトル */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          フィードバック・お問い合わせ
        </h1>

        {/* 説明文 */}
        <div className="mb-8 text-gray-700 leading-relaxed space-y-3">
          <p>
            キョウコレは現在<strong>β版</strong>として公開しています。
            バグ報告・改善提案・使いにくかった点など、どんなことでもお気軽にお寄せください。
          </p>
          <p>
            返信が必要な場合は、フォーム内の「メールアドレス」欄にご記入ください。
          </p>
        </div>

        {/* 成功メッセージ */}
        {submitStatus === 'success' && (
          <div className="mb-6 rounded-lg border border-teal-200 bg-teal-50 p-4 text-teal-800">
            <div className="flex items-start">
              <i className="ri-check-line text-xl mr-2 flex-shrink-0"></i>
              <div>
                <p className="font-medium">フィードバックを送信しました</p>
                <p className="text-sm mt-1">
                  貴重なご意見をありがとうございます。今後のサイト改善に活用させていただきます。
                </p>
              </div>
            </div>
          </div>
        )}

        {/* エラーメッセージ */}
        {submitStatus === 'error' && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
            <div className="flex items-start">
              <i className="ri-error-warning-line text-xl mr-2 flex-shrink-0"></i>
              <div>
                <p className="font-medium">送信に失敗しました</p>
                <p className="text-sm mt-1">
                  時間をおいて再度お試しください。問題が続く場合は、別の方法でお問い合わせください。
                </p>
              </div>
            </div>
          </div>
        )}

        {/* フォーム */}
        <form 
          onSubmit={handleSubmit}
          data-readdy-form
          className="bg-white border border-gray-200 rounded-lg p-6 space-y-6"
        >
          {/* お名前（任意） */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              お名前（任意）
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-colors"
              placeholder="山田太郎"
            />
          </div>

          {/* メールアドレス（任意） */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              メールアドレス（任意・返信希望時）
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-colors"
              placeholder="example@example.com"
            />
          </div>

          {/* フィードバック内容（必須） */}
          <div>
            <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
              フィードバック内容<span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              id="feedback"
              name="feedback"
              value={formData.feedback}
              onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
              rows={8}
              maxLength={500}
              required
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-colors resize-none"
              placeholder="バグ報告、改善提案、使いにくかった点など、どんなことでもお気軽にお書きください。"
            />
            <p className="mt-1 text-xs text-gray-500 text-right">
              {formData.feedback.length} / 500文字
            </p>
          </div>

          {/* 送信ボタン */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting || !formData.feedback.trim()}
              className="inline-flex items-center rounded-lg bg-teal-600 px-6 py-3 text-base font-medium text-white hover:bg-teal-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  送信中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line mr-2"></i>
                  送信する
                </>
              )}
            </button>
          </div>
        </form>

        {/* 補足情報 */}
        <div className="mt-8 text-sm text-gray-600 leading-relaxed">
          <p>
            <i className="ri-information-line mr-1"></i>
            いただいたフィードバックは、サイトの改善に活用させていただきます。
            個別の返信をお約束するものではありませんが、できる限り対応させていただきます。
          </p>
        </div>
      </div>
    </Layout>
  );
}
